// Which server this console talks to, chosen by name from a list rather than
// typed as an address.
//
// THE ADDRESSES ARE NOT IN THIS FILE, AND THAT IS THE DECISION HERE.
//
// They are imported from xp/app/src/server/environments.ts, which is the
// tablet's table. After Phase 4 the console talks to `adminUrl` and the tablet
// talks to `baseUrl`. Until an environment's admin service is deployed,
// `adminUrl` is null and this file falls back to `baseUrl` — the combined
// process — rather than building against a hostname that 404s.
//
// It is the same shape as the ruling on the design system, which is why it
// reads consistently: import the values that must not disagree, define the
// rules that must differ. Colour comes from XP1 and density does not; the
// address comes from the tablet's table and the wording, the refusals and the
// mark do not.
//
// WHAT DELIBERATELY DOES NOT COME ACROSS WITH IT.
//
// `readEnvironment` in that file is the tablet's, and its sentences say
// "tablet", name the tablet's build scripts, and point at the tablet's source
// file. A crew member reading a tablet's fault screen and a client reading a
// console are different people with different next actions, so calling it here
// would put the wrong instruction in front of both.
//
// The address override is not carried across either. The tablet keeps
// VITE_NOVA_BASE_URL because its harness binds a port that moves and because
// that variable is what fingers already type — its own file argues both. A
// console has neither reason, and an override is a second way to aim the thing
// that shows a Nova admin every client's attendee list. If the harness's port
// moves, it moves in the table, in a diff somebody reads.
//
// Nothing here may touch `import.meta`, because vite.config.ts imports this
// module and runs it in Node. thisBuild.ts is the half that reads the variable.

import { ENVIRONMENTS as XP_SERVERS, type KnownEnvironment } from "../../../app/src/server/environments.js";

/** The variable that names a server. One string, so the check can find it. */
export const ENV_VARIABLE = "VITE_XP_ENV";

/**
 * The variable the tablet still reads, named here only to refuse it.
 *
 * A build made with the tablet's command — the one habit most likely to arrive
 * here, because it is the command already in somebody's shell history — is
 * refused with a sentence saying so rather than succeeding while ignoring it. A
 * variable that is present and disregarded is the worst of the three outcomes:
 * whoever set it believes it took effect.
 */
export const REFUSED_ADDRESS_VARIABLE = "VITE_NOVA_BASE_URL";

export type ServerName = keyof typeof XP_SERVERS;

/** In the order a person meets them, which is the order they are printed in. */
export const SERVER_NAMES = Object.keys(XP_SERVERS) as ServerName[];

export type ServerReading =
  | {
      readonly ok: true;
      readonly name: ServerName;
      readonly baseUrl: string;
      /**
       * Where screens fetch a picture or a film. The tablet table's `baseUrl`,
       * which is the public API. After Phase 4 the console talks to admin
       * (`baseUrl` on this reading) and `/v1/kiosk/assets/:id` lives on this
       * origin, not that one. A preview aimed at admin is a Fastify 404.
       */
      readonly apiUrl: string;
      /**
       * The public registration host this build may print, or null where it
       * must not. Threaded from the tablet table's `webOrigin`. Production is
       * the sending domain: the console prints `/j/<activation>` on that host.
       */
      readonly webOrigin: string | null;
      /**
       * The kiosk player host this build may print, or null where it must not.
       * Threaded from the tablet table's `kioskOrigin`. Production is the
       * Hosting site behind kiosk.novaexhibits.com.
       */
      readonly kioskOrigin: string | null;
      /**
       * Whether the numbers on this console are somebody's real event.
       *
       * The tablet's field of the same name decides whether a capture screen is
       * watermarked. Read from this end it decides the opposite direction of the
       * same fact: whether what is drawn here is a report or a rehearsal.
       */
      readonly holdsRealAttendeeData: boolean;
      /** What this server is, in the tablet table's own words. */
      readonly what: string;
    }
  /**
   * One sentence, written to be read in two places: thrown by the build, and
   * drawn on the console's own refusal screen. Unlike the tablet's, the second
   * reader here may be a client rather than a colleague, so it says what is
   * wrong and does not ask them to do anything about it.
   */
  | { readonly ok: false; readonly problem: string };

/**
 * Resolve the build-time variable into a server, or into a refusal.
 *
 * Pure, and takes both values as arguments rather than reading them, so that
 * the build tool, the application and the check all drive the same code over
 * inputs they choose. `address` is taken only so that it can be refused.
 */
export function readServer(name: string | undefined, address?: string | undefined): ServerReading {
  const chosen = (name ?? "").trim();

  if (chosen === "") {
    // No default, and the argument is xp/app/src/server/environments.ts's,
    // sharpened by what this surface is. A default of staging turns a forgotten
    // variable from a console that cannot work into a console that works and is
    // aimed at rehearsal data, and a client reading rehearsal numbers as though
    // they were their event is the failure the roadmap calls the wrong-server
    // problem in its most embarrassing form. A default of local produces a
    // console that cannot reach anything, which is the cheap failure with a
    // friendlier address.
    return {
      ok: false,
      problem: `This console build does not say which server it belongs to. ${ENV_VARIABLE} was not set, so nothing decided which one it reads from. The names are ${list(SERVER_NAMES)}.`,
    };
  }

  const known = (XP_SERVERS as Record<string, KnownEnvironment | undefined>)[chosen];

  if (known === undefined) {
    // The likeliest wrong value is not a misspelling, it is an address pasted
    // where a name goes. Said separately, because "staging is not one of local,
    // staging, production" is a confusing sentence to read when what you typed
    // was a URL.
    return {
      ok: false,
      problem: /^[a-z]+:\/\//i.test(chosen)
        ? `${ENV_VARIABLE} was given an address rather than the name of a server. The names are ${list(SERVER_NAMES)}. An address that ought to be buildable belongs in ENVIRONMENTS in xp/app/src/server/environments.ts, which is where both halves of XP1 read it from.`
        : `${ENV_VARIABLE} names ${JSON.stringify(chosen)}, which is not a server XP1 knows. The names are ${list(SERVER_NAMES)}.`,
    };
  }

  const consoleUrl = known.adminUrl ?? known.baseUrl;
  if (consoleUrl === null) {
    return {
      ok: false,
      problem: `${ENV_VARIABLE} names ${chosen}, and there is no ${chosen} deployment of XP1 yet. Its address is null in xp/app/src/server/environments.ts and in xp/xp.config.json, and a null there is refused rather than guessed at.`,
    };
  }

  if (address !== undefined && address.trim() !== "") {
    return {
      ok: false,
      problem: `${REFUSED_ADDRESS_VARIABLE} was set as well as ${ENV_VARIABLE}=${chosen}. The console takes no address: every server it may read from has a name, and a name is the thing it can put on its own screen. That variable is the tablet's, and this is not the tablet.`,
    };
  }

  return {
    ok: true,
    name: chosen as ServerName,
    baseUrl: consoleUrl,
    apiUrl: known.baseUrl ?? consoleUrl,
    webOrigin: known.webOrigin,
    kioskOrigin: known.kioskOrigin,
    holdsRealAttendeeData: known.allowsRealAttendeeData,
    what: known.what,
  };
}

/**
 * The registration origin a named environment carries. Read off the table
 * rather than off a resolved build, so a check can assert the host without
 * needing a production console artefact.
 */
export function registrationOrigin(name: ServerName): string | null {
  return XP_SERVERS[name].webOrigin;
}

/**
 * The kiosk player origin a named environment carries, including production's
 * null. Read off the table rather than off a resolved build, so a check can
 * assert production refuses without needing a production console artefact.
 */
export function kioskPlayerOrigin(name: ServerName): string | null {
  return XP_SERVERS[name].kioskOrigin;
}

/**
 * What the permanent mark says, or null where there should not be one.
 *
 * THE WORDING AVOIDS THE WORD STAGING, DELIBERATELY.
 *
 * The roadmap asks for an answer "legible to somebody who has never heard the
 * word staging", and the reader here is not a colleague: it is a client looking
 * at what they believe are their event's numbers. The tablet's watermark can say
 * STAGING SERVER because the only person who ever sees it works here. This says
 * what is untrue about the screen instead of naming the machine that made it
 * untrue, and it names the server afterwards for whoever does know the word.
 *
 * Null only where the server holds real attendee data. A build that resolved to
 * nothing is marked too, so the rule has no exception for anybody to remember.
 */
export function consoleMark(reading: ServerReading): string | null {
  const parts = consoleMarkParts(reading);
  if (parts === null) return null;
  return [parts.label, parts.sentence, parts.server].filter((part) => part !== null).join(" ");
}

/**
 * The same mark, in the three pieces the strip is set in.
 *
 * THE STRIP IS NOT ONE RUN OF TEXT AND NEVER READ AS ONE. The label is the word
 * somebody catches without reading — it carries the letter-spacing and the full
 * amber — the sentence is the explanation, and the server is the detail that
 * matters only once the first two have landed, so it is drawn quieter. A single
 * string cannot say any of that, and the strip rendered as one was the "still
 * different" in the screenshots: right words, wrong three weights.
 *
 * consoleMark above composes from this rather than restating it, so the sentence
 * a check finds on a surface and the sentence a reader sees cannot drift apart.
 * A build that names no server has no label and no server — the whole of it is
 * the sentence, because "NOT CONNECTED" as a heading would be a word this
 * function invented for a case that already says what is wrong.
 */
export type ConsoleMarkParts = {
  /** The word set apart, or null where the whole mark is one sentence. */
  readonly label: string | null;
  readonly sentence: string;
  /** Which server, in brackets, or null where there is none to name. */
  readonly server: string | null;
};

export function consoleMarkParts(reading: ServerReading): ConsoleMarkParts | null {
  if (!reading.ok) {
    return {
      label: null,
      sentence: "This console is not connected to anything. Nothing below is a number.",
      server: null,
    };
  }
  if (reading.holdsRealAttendeeData) return null;
  return {
    label: "PRACTICE DATA",
    sentence: ": these are not your event's real numbers, and nobody on this screen is a real attendee",
    server: `(${reading.name}, ${host(reading.baseUrl)})`,
  };
}

/**
 * The same fact in a sentence, for the screen that confirms rather than warns.
 *
 * Said on the live server too, unlike the mark, on the tablet file's reasoning:
 * a panel that names the server only when it is the wrong one cannot be used to
 * confirm that it is the right one, and confirming is what somebody setting a
 * console up is actually doing.
 */
export function serverSentence(reading: ServerReading): string {
  if (!reading.ok) {
    return "This console build does not name a server, so it cannot reach one. It has to be built again.";
  }
  return `Reading from the ${reading.name} server: ${reading.what}, at ${host(reading.baseUrl)}.`;
}

/** Host and port, because the scheme is the same on every one of them. */
function host(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

const list = (names: readonly string[]): string =>
  names.length < 2
    ? (names[0] ?? "none")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
